import {
    ChangeDetectionStrategy, AfterViewInit,
    Component,
    ElementRef,
    inject,
    input,
    model,
    NgZone,
    OnChanges,
    OnDestroy,
    output,
    SimpleChanges,
    ViewEncapsulation,
    } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs/esm/index';
import { FtDaterangeCalendar } from './ft-daterange-calendar';

/**
 * Native port of the AngularJS `ftDatePicker` element directive: a single-date dropdown backed by
 * {@link FtDaterangeCalendar} in `singleDatePicker` mode. Reproduces the v1 markup and the two
 * different types the one callback produces — `date` is a `YYYY-MM-DD` STRING, `dateChanged` emits a
 * native `Date`.
 *
 * `ViewEncapsulation.None` so the global `.ft-dropdown` / `.daterangepicker` styles apply.
 */
@Component({
    selector: 'ft-date-picker-ng',
    imports: [],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './ft-date-picker.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FtDatePickerComponent implements AfterViewInit, OnChanges, OnDestroy {
    /** Two-way: the selected date string (data format YYYY-MM-DD), v1 `date="..."`. */
    readonly date = model<string>();
    /** v1 `position` → the calendar's `opens`. */
    readonly position = input<string>();
    /** v1 `on-date-changed` (emits the native Date). */
    readonly dateChanged = output<Date>();

    protected readonly setDateLabel: string;

    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly zone = inject(NgZone);
    private picker: FtDaterangeCalendar | null = null;
    private current: dayjs.Dayjs;
    private readonly dataFormat = 'YYYY-MM-DD';
    private readonly displayFormat = 'MMMM D, YYYY';

    constructor() {
        this.setDateLabel = inject(TranslateService).instant('flipto.components.common.dateRange.SetDate');
        this.current = this.date() ? dayjs(this.date()) : dayjs();
    }

    ngAfterViewInit(): void {
        const options: Record<string, unknown> = { startDate: this.current, singleDatePicker: true };
        const position = this.position();
        if (position != null) options['opens'] = position;
        this.picker = new FtDaterangeCalendar(this.host.nativeElement, options, (start) => {
            this.zone.run(() => {
                this.current = start;
                this.date.set(start.format(this.dataFormat));
                this.dateChanged.emit(start.toDate());
                this.showDate(start);
            });
        });
        // v1 did NOT paint the label on init — `showDate()` was only reachable from the change
        // callback and from a non-first ngOnChanges — so the trigger keeps reading "Set date" even
        // when `date` already holds a value. details-body.component.html:125 binds an incentive
        // expiry that is ALWAYS populated, so painting it here would be a visible divergence on a
        // surface the parity gate runs at maxDiffPct: 1. Left deliberately unpainted.
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['date'] && !changes['date'].firstChange && this.date()) {
            this.current = dayjs(this.date());
            this.showDate(this.current);
        }
    }

    ngOnDestroy(): void {
        this.picker?.remove();
        this.picker = null;
    }

    /**
     * v1 wrote the label straight into the host span, clobbering the `{{ setDateLabel }}`
     * interpolation — kept verbatim, because the template still interpolates and an Angular binding
     * here would fight it.
     *
     * v1 then called `initialize()` again, which built a WHOLE NEW jQuery picker on the same element
     * and leaked the previous body-appended calendar every time. `setOptions` is the faithful
     * equivalent — it re-seeds the same instance's start date — without the leak.
     */
    private showDate(date: dayjs.Dayjs): void {
        if (date == null) return;
        const span = this.host.nativeElement.querySelector('span');
        if (span) span.innerHTML = date.format(this.displayFormat);
        this.picker?.setOptions({ startDate: date, singleDatePicker: true, opens: this.position() as never });
    }
}
